import React from "react";
import { container } from "@/utils/mobx";
import { Dialog } from "@/components/toolkit/Dialog";
import { Clickable } from "@/components/toolkit/Clickable";
import { SelectInput, SelectInputOption } from "@/components/toolkit/SelectInput";
import { AgentPaneStore } from "./store";
import { t, TranslationKey } from "@/i18n";

// Localize the effort levels we know; agents may report others (pi: off/minimal), shown as-is.
const EFFORT_LABELS: Record<string, TranslationKey> = {
    low: "settings_claude_effort_low",
    medium: "settings_claude_effort_medium",
    high: "settings_claude_effort_high",
    max: "settings_claude_effort_max",
};

export const SessionSettingsModal = container((props: {
    store: AgentPaneStore;
}) => {
    const { store } = props;
    const id = store.sessionSettingsId;
    if (!id) return null;

    const session = store.agentSessions.find(s => s.id === id);
    const settings = store.tabSettingsFor(id);
    const loading = session?.options_loading ?? false;
    const error = store.sessionSettingsError ?? session?.options_error ?? null;

    const backendOptions: SelectInputOption[] = (session?.available_backends ?? []).map(b => ({ value: b.value, title: b.name }));

    const defaultModelTitle = session?.current_model && !settings.model
        ? `${t("settings_claude_model_default")} (${session.current_model})`
        : t("settings_claude_model_default");
    const modelOptions: SelectInputOption[] = [
        { value: "", title: defaultModelTitle },
        ...(session?.available_models ?? []).map(m => ({
            value: m.value,
            title: m.name,
            group: m.group,
            // Show the raw id when it differs from the label, so search by id is discoverable.
            hint: m.name !== m.value ? m.value : undefined,
        })),
    ];
    // Keep a persisted model selectable/visible even if the agent no longer lists it.
    if (settings.model && !modelOptions.some(o => o.value === settings.model)) {
        modelOptions.push({ value: settings.model, title: settings.model });
    }

    const efforts = session?.available_efforts ?? [];
    const effortLabel = (value: string, name: string) => (EFFORT_LABELS[value] ? t(EFFORT_LABELS[value]) : name);
    const defaultEffortTitle = session?.current_effort && !settings.effort
        ? `${t("settings_claude_effort_default")} (${effortLabel(session.current_effort, session.current_effort)})`
        : t("settings_claude_effort_default");

    return (
        <Dialog title={t("agent_session_settings")} onClose={() => store.closeSessionSettings()}>
            <div className="agent_pane__session_settings">
                <div className="agent_pane__session_settings_section">
                    <div className="agent_pane__session_settings_label">{t("agent_backend")}</div>
                    <SelectInput
                        className="agent_pane__session_settings_select"
                        value={settings.backend}
                        options={backendOptions}
                        onChange={(backend) => backend && backend !== settings.backend && store.changeSessionBackend(id, backend)}
                    />
                </div>
                <div className="agent_pane__session_settings_section">
                    <div className="agent_pane__session_settings_label">{t("settings_claude_model")}</div>
                    <SelectInput
                        className="agent_pane__session_settings_select"
                        value={settings.model ?? ""}
                        options={modelOptions}
                        onChange={(model) => store.updateSessionSettings(id, { model: model || null })}
                        placeholder={loading ? t("settings_model_loading") : undefined}
                        searchable
                        searchPlaceholder={t("settings_model_search")}
                        emptyText={loading ? t("settings_model_loading") : t("settings_model_none")}
                        allowCustom
                        customTitle={(q) => `${t("settings_model_custom")} ${q}`}
                    />
                    {loading && <div className="agent_pane__session_settings_status">{t("settings_model_loading")}</div>}
                    {error && <div className="agent_pane__session_settings_status agent_pane__session_settings_status--error">{error}</div>}
                </div>
                {efforts.length > 0 && (
                    <div className="agent_pane__session_settings_section">
                        <div className="agent_pane__session_settings_label">{t("settings_claude_effort")}</div>
                        <div className="agent_pane__session_settings_options">
                            {[{ value: "", name: defaultEffortTitle }, ...efforts.map(e => ({ value: e.value, name: effortLabel(e.value, e.name) }))].map(({ value, name }) => (
                                <Clickable key={value} onClick={() => store.updateSessionSettings(id, { effort: value || null })}>
                                    <button
                                        className={`agent_pane__session_settings_btn${(settings.effort ?? "") === value ? " agent_pane__session_settings_btn--active" : ""}`}
                                    >
                                        {name}
                                    </button>
                                </Clickable>
                            ))}
                        </div>
                    </div>
                )}
            </div>
        </Dialog>
    );
}, AgentPaneStore);
